'use client'

interface PerformanceMetrics {
  metrics: Record<string, number>
}

export async function getPerformanceMetrics(): Promise<PerformanceMetrics> {
  const metrics: Record<string, number> = {}

  try {
    if (!window.performance) {
      return {metrics}
    }
  } catch (e) {
    return {metrics}
  }

  // Get immediate timing metrics using modern Performance API
  try {
    // Navigation timing using PerformanceNavigationTiming
    const navigationEntries = window.performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
    if (navigationEntries.length > 0) {
      const navigation = navigationEntries[0]

      // Page load time
      if (navigation.loadEventEnd > 0) {
        metrics['pf-page-load-time'] = Math.round(navigation.loadEventEnd)
      }

      // DOM content loaded
      if (navigation.domContentLoadedEventEnd > 0) {
        metrics['pf-dom-content-loaded'] = Math.round(navigation.domContentLoadedEventEnd)
      }

      // Time to Interactive (using domInteractive)
      if (navigation.domInteractive > 0) {
        metrics['pf-time-to-interactive'] = Math.round(navigation.domInteractive)
      }
    }

    // First Contentful Paint
    const paintEntries = window.performance.getEntriesByType('paint')
    const fcpEntry = paintEntries.find((entry) => entry.name === 'first-contentful-paint')
    if (fcpEntry) {
      metrics['pf-first-contentful-paint'] = Math.round(fcpEntry.startTime)
    }

    // First Paint
    const fpEntry = paintEntries.find((entry) => entry.name === 'first-paint')
    if (fpEntry) {
      metrics['pf-first-paint'] = Math.round(fpEntry.startTime)
    }
  } catch (e) {
    // Performance API not available
  }

  // Set up observers with timeout
  const observerPromises: Promise<void>[] = []

  try {
    if ('PerformanceObserver' in window) {
      // LCP Observer Promise
      const lcpPromise = new Promise<void>((resolve) => {
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries()
          const lastEntry = entries[entries.length - 1]
          metrics['pf-largest-contentful-paint'] = Math.round(lastEntry.startTime)
          lcpObserver.disconnect()
          resolve()
        })

        try {
          lcpObserver.observe({entryTypes: ['largest-contentful-paint']})
        } catch (e) {
          resolve()
        }
      })

      // CLS Observer Promise
      const clsPromise = new Promise<void>((resolve) => {
        let clsValue = 0
        const clsObserver = new PerformanceObserver((entryList) => {
          for (const entry of entryList.getEntries()) {
            if (!(entry as any).hadRecentInput) {
              clsValue += (entry as any).value
            }
          }
          metrics['pf-cumulative-layout-shift'] = Number(clsValue.toFixed(4))
        })

        try {
          clsObserver.observe({entryTypes: ['layout-shift']})
          // CLS is tracked continuously, resolve after a short delay
          setTimeout(() => {
            clsObserver.disconnect()
            resolve()
          }, 2000)
        } catch (e) {
          resolve()
        }
      })

      observerPromises.push(lcpPromise, clsPromise)
    }
  } catch (e) {
    // PerformanceObserver not supported
  }

  // Wait for observers with 10-second timeout
  try {
    await Promise.race([Promise.all(observerPromises), new Promise<void>((resolve) => setTimeout(resolve, 10000))])
  } catch (e) {
    // Timeout or observer error
  }

  return {metrics}
}
